import { Link } from "react-router-dom";
import { Instagram, Linkedin, Twitter, Youtube, Music2, Mail, CalendarClock } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";

const Label = ({ children, dark }) => (
  <span
    className={`text-xs font-semibold uppercase tracking-[0.16em] ${
      dark ? "text-[var(--mint)]" : "text-[var(--ember)]"
    }`}
  >
    {children}
  </span>
);

/* ------------------------------------------------------------------ *
 * FOUNDER PLACEHOLDERS — swap these with the real content.
 * ------------------------------------------------------------------ */
const FOUNDER = {
  name: "Tamer Abdel",
  portrait: `${process.env.PUBLIC_URL}/team/tamer-abdel.jpg`, // founder portrait
  visionOneLiner:
    "I started The AI Dept to give every business the AI advantage without the hype — real experts, real results.", // [REPLACE WITH TAMER'S WORDS]
  email: "tamer@theaidept.net", // [EMAIL PLACEHOLDER]
  bookingLink: "https://example.com", // [CALENDAR LINK PLACEHOLDER]
};

const IG_HANDLE = "@ChiefAIOfficer";
const IG_PROFILE = "https://www.instagram.com/chiefaiofficer/";
const IG_SCREENSHOT = `${process.env.PUBLIC_URL}/team/chiefaiofficer-ig.png`;

// Founder socials — placeholder URLs, editable. TikTok uses a music glyph.
const FOUNDER_SOCIALS = [
  { label: "Instagram", Icon: Instagram, url: "https://example.com" },
  { label: "LinkedIn", Icon: Linkedin, url: "https://example.com" },
  { label: "X / Twitter", Icon: Twitter, url: "https://example.com" },
  { label: "YouTube", Icon: Youtube, url: "https://example.com" },
  { label: "TikTok", Icon: Music2, url: "https://example.com" },
];
/* ------------------------------------------------------------------ */

export default function About() {
  return (
    <>
      {/* 1. Founder hero */}
      <Hero
        image={FOUNDER.portrait}
        video="/videos/about-hero.mp4"
        eyebrow="FOUNDER & CEO"
        title="Tamer Abdel."
        subline={FOUNDER.visionOneLiner}
        primary={{ label: "Get in touch", href: "#reach" }}
      />

      {/* 2. Founder profile — who is Tamer Abdel */}
      <section className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="about-profile">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal><Label>The founder</Label></Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section" data-testid="about-profile-heading">
                A builder who's done it before.
              </h2>
            </Reveal>
          </div>

          <div className="mt-10 max-w-3xl">
            <Reveal delay={140}>
              <p className="text-base leading-relaxed text-[var(--muted)] sm:text-lg" data-testid="about-profile-bio-1">
                Tamer Abdel is a technology entrepreneur with more than 25 years
                of experience building and scaling software companies. He has
                founded and successfully exited three startups and has partnered
                with more than 500 brands and early-stage startups to accelerate
                growth through technology, marketing, and product innovation.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-5 text-base leading-relaxed text-[var(--muted)] sm:text-lg" data-testid="about-profile-bio-2">
                He has led the development of more than 10 technology products
                across SaaS, marketplaces, AI, and marketing platforms that have
                collectively generated more than $50 million in revenue. His
                consulting experience includes global brands such as Pepsi,
                McCormick, Puma, and Facebook, where he has advised on branding,
                market positioning, marketing strategy, and technology. Tamer
                combines expertise in software engineering, AI, product strategy,
                UX, and brand development to help businesses turn ambitious ideas
                into scalable products and high-growth companies.
              </p>
            </Reveal>
          </div>

          {/* Stat highlights */}
          <Reveal delay={260}>
            <div className="mt-14 grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-4" data-testid="about-profile-stats">
              {[
                { n: "25+", l: "Years building software" },
                { n: "3", l: "Startups founded & exited" },
                { n: "500+", l: "Brands & startups partnered" },
                { n: "$50M+", l: "Revenue generated" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="text-3xl font-semibold tracking-tight text-[var(--ink)] sm:text-4xl">{s.n}</p>
                  <p className="mt-2 text-[13px] leading-snug text-[var(--muted)]">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Brands advised */}
          <Reveal delay={320}>
            <div className="mt-14 border-t border-black/10 pt-6" data-testid="about-profile-brands">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--muted)]">
                Advised brands including
              </p>
              <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-lg font-semibold tracking-tight text-[var(--ink)]/80">
                <span>Pepsi</span>
                <span>McCormick</span>
                <span>Puma</span>
                <span>Facebook</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 3. Founder video block */}
      <section className="bg-[var(--bg-contrast)] py-28 md:py-36" data-testid="about-video">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal><Label>In his words</Label></Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section">Why I'm building The AI Dept.</h2>
            </Reveal>
          </div>
          <Reveal delay={140}>
            <div className="group relative mt-12 aspect-video overflow-hidden rounded-3xl border border-black/5 bg-[var(--charcoal)]">
              <iframe
                className="h-full w-full"
                src="https://www.youtube.com/embed/6I18B4p1LmQ?rel=0&modestbranding=1"
                title="Why I'm building The AI Dept."
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="founder-video"
              />
            </div>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-4 text-sm text-[var(--muted)]">
              [Optional caption — a line about the video.]
            </p>
          </Reveal>
        </div>
      </section>

      {/* 4. Instagram promotion — follow @ChiefAIOfficer */}
      <section className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="about-instagram">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Copy + CTA */}
            <div className="max-w-xl">
              <Reveal><Label>On Instagram</Label></Reveal>
              <Reveal delay={80}>
                <h2 className="mt-7 t-section">Follow me on Instagram.</h2>
              </Reveal>
              <Reveal delay={140}>
                <p className="mt-6 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                  I post daily as <span className="font-semibold text-[var(--ink)]">Chief AI Officer</span> —
                  challenging conventional wisdom with AI, showing what we build inside The AI Dept.,
                  and helping you stay employable in a world that's changing fast.
                </p>
              </Reveal>
              <Reveal delay={200}>
                <div className="mt-9 flex flex-wrap items-center gap-5">
                  <Button
                    href={IG_PROFILE}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary"
                    data-testid="ig-follow-link"
                  >
                    <Instagram size={18} /> Follow {IG_HANDLE}
                  </Button>
                  <span className="text-sm font-medium text-[var(--muted)]">2,500+ followers</span>
                </div>
              </Reveal>
            </div>

            {/* Profile screenshot */}
            <Reveal delay={160}>
              <a
                href={IG_PROFILE}
                target="_blank"
                rel="noopener noreferrer"
                className="group block overflow-hidden rounded-3xl border border-black/5 bg-[var(--charcoal)] shadow-[0_30px_70px_-30px_rgba(0,0,0,0.4)]"
                data-testid="ig-screenshot"
              >
                <img
                  src={IG_SCREENSHOT}
                  alt="Chief AI Officer on Instagram — @ChiefAIOfficer"
                  loading="lazy"
                  className="w-full transition-transform duration-500 group-hover:scale-[1.02]"
                />
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5. Direct contact + socials */}
      <section id="reach" className="scroll-mt-24 bg-[var(--charcoal)] py-28 text-white md:py-36" data-testid="about-reach">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
          <Reveal><Label dark>Reach me directly</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mx-auto mt-7 max-w-2xl t-section text-white">Talk to me, not a form.</h2>
          </Reveal>
          <Reveal delay={140}>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button href={`mailto:${FOUNDER.email}`} variant="primary" data-testid="founder-email">
                <Mail size={17} className="mr-2 inline" />
                {FOUNDER.email}
              </Button>
              <Button href={FOUNDER.bookingLink} variant="secondaryDark" data-testid="founder-booking">
                <CalendarClock size={17} className="mr-2 inline" />
                Book time with me
              </Button>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-12 flex items-center justify-center gap-3">
              {FOUNDER_SOCIALS.map(({ label, Icon, url }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-white/70 transition-all duration-300 hover:border-[var(--mint)]/50 hover:text-[var(--mint)]"
                  data-testid={`social-${label.split(" ")[0].toLowerCase()}`}
                >
                  <Icon size={19} />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. Closing sign-off */}
      <section className="bg-[var(--bg-base)] py-16" data-testid="about-signoff">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
          <Reveal>
            <p className="text-base text-[var(--muted)] sm:text-lg">
              One AI department. Every specialist. Built by people who answer for
              the result.{" "}
              <Link to="/" className="font-semibold text-[var(--ember)] hover:underline">
                See how it works →
              </Link>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
